import { memo } from "react";
import { NavLink, Link } from "react-router-dom";
import { User, LogOut, Settings as SettingsIcon, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { useLang } from "@/context/LanguageContext";
import BusinessSwitcher from "@/components/BusinessSwitcher";
import { logoHorizontalDark, emblem } from "@/assets/brand";

// Live "Your Capabilities" group — dynamic Workshop capability names must NOT be run through the
// static translation dictionary (they're tenant-specific), everything else is translated via t().
const CAP_GROUP = "Your Capabilities";

const TIP_CLASS = "bg-[#1A1A1D] text-white/90 border border-white/10 font-medium shadow-xl";

// A single navigation row. Anchored so the icon barely moves between expanded/collapsed states;
// the label fades + slides away (max-width + opacity) rather than popping. Memoised so the row only
// re-renders when its own props change — the sidebar no longer remounts on unrelated Layout ticks.
const RailItem = memo(function RailItem({ to, label, Icon, badge = 0, collapsed, testid, onNavigate, translate = true }) {
  const { t } = useLang();
  const text = translate ? t(label) : label;
  const link = (
    <NavLink to={to} data-testid={testid} onClick={onNavigate}
      className={({ isActive }) => `lux-nav ${isActive ? "is-active" : ""}`}>
      <Icon className="w-[18px] h-[18px] shrink-0" />
      <span className={`whitespace-nowrap overflow-hidden transition-all duration-200 ${collapsed ? "max-w-0 opacity-0" : "flex-1 max-w-[190px] opacity-100"}`}>{text}</span>
      {badge > 0 && !collapsed && (
        <span className="bg-[#D4AF37] text-[#0A0A0B] text-[10px] font-bold rounded-full min-w-5 h-5 px-1.5 flex items-center justify-center shrink-0">{badge}</span>
      )}
      {badge > 0 && collapsed && (
        <span className="absolute top-1.5 right-2 w-2 h-2 rounded-full bg-[#D4AF37] shadow-[0_0_6px_rgba(212,175,55,0.7)]" aria-hidden />
      )}
    </NavLink>
  );
  if (!collapsed) return link;
  return (
    <Tooltip>
      {/* Wrap in a span trigger (not asChild-on-NavLink): Radix's Slot would otherwise clobber
          NavLink's function-form className and drop the `is-active` class in collapsed mode. */}
      <TooltipTrigger asChild>
        <span className="block">{link}</span>
      </TooltipTrigger>
      <TooltipContent side="right" sideOffset={12} className={TIP_CLASS}>{text}</TooltipContent>
    </Tooltip>
  );
});

// Footer button (Sign out / collapse) — same premium row treatment, tooltip when collapsed.
function RailButton({ label, Icon, collapsed, onClick, testid }) {
  const btn = (
    <button data-testid={testid} onClick={onClick} className="lux-nav w-full">
      <Icon className="w-[18px] h-[18px] shrink-0" />
      <span className={`whitespace-nowrap overflow-hidden transition-all duration-200 ${collapsed ? "max-w-0 opacity-0" : "flex-1 max-w-[190px] opacity-100 text-left"}`}>{label}</span>
    </button>
  );
  if (!collapsed) return btn;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{btn}</TooltipTrigger>
      <TooltipContent side="right" sideOffset={12} className={TIP_CLASS}>{label}</TooltipContent>
    </Tooltip>
  );
}

function SidebarInner({ user, nav, unreadMsgs = 0, collapsed = false, onToggle, onNavigate, onLogout, showToggle = true }) {
  const { t } = useLang();
  const teamMember = ["owner", "manager", "staff"].includes(user.role);
  return (
    <TooltipProvider delayDuration={80} skipDelayDuration={300}>
      <div className="flex flex-col h-full relative">
        <div aria-hidden className="pointer-events-none absolute -top-20 -left-12 w-64 h-64 rounded-full bg-[#D4AF37]/[0.07] blur-3xl" />

        {/* Brand */}
        <div className={`relative ${collapsed ? "px-2 flex justify-center" : "px-6"} pt-7 pb-5 after:content-[''] after:absolute after:bottom-0 ${collapsed ? "after:left-3 after:right-3" : "after:left-6 after:right-6"} after:h-px after:bg-gradient-to-r after:from-transparent after:via-[#D4AF37]/25 after:to-transparent`}>
          {collapsed ? (
            <img src={emblem} alt="ARI" data-testid="sidebar-logo" className="h-8 w-8 object-contain select-none" />
          ) : (
            <div className="min-w-0">
              <span className="flex items-center h-8" data-testid="sidebar-logo">
                <img src={logoHorizontalDark} alt="Admin Restaurant Intelligence" className="h-full w-auto object-contain select-none" />
              </span>
              <p className="text-[10px] uppercase tracking-widest text-white/40 mt-2 truncate">{user.restaurant || (user.role === "salesperson" ? "Sales Team" : user.role === "super_admin" ? "Platform Console" : "Workspace")}</p>
              {teamMember && <BusinessSwitcher />}
            </div>
          )}
        </div>

        {/* Navigation — one continuous list; clusters separated by a quiet hairline, no headings */}
        <nav className={`flex-1 ${collapsed ? "px-3" : "px-3.5"} pb-4 overflow-y-auto overflow-x-hidden pm-scroll`}>
          {nav.map((section, si) => (
            <div key={section.group || `s-${si}`}>
              {si > 0 && <div className="h-px bg-white/[0.06] mx-2 my-2.5" aria-hidden />}
              <div className="space-y-1">
                {section.items.map((n) => (
                  <RailItem key={n.to} to={n.to} label={n.label} Icon={n.icon}
                    badge={n.to === "/messages" ? unreadMsgs : 0}
                    collapsed={collapsed} onNavigate={onNavigate}
                    translate={section.group !== CAP_GROUP}
                    testid={n.testid || `nav-${n.label.toLowerCase().replace(" ", "-")}`} />
                ))}
              </div>
            </div>
          ))}
        </nav>

        {/* Footer — profile / settings / sign out / collapse control */}
        <div className={`${collapsed ? "px-3" : "px-3.5"} pt-4 pb-5 relative before:content-[''] before:absolute before:top-0 before:left-4 before:right-4 before:h-px before:bg-white/[0.05] space-y-1`}>
          <RailItem to="/profile" label="Profile" Icon={User} collapsed={collapsed} onNavigate={onNavigate} testid="nav-profile" />
          {teamMember && (
            <RailItem to="/settings" label="Settings" Icon={SettingsIcon} collapsed={collapsed} onNavigate={onNavigate} testid="nav-settings" />
          )}
          <RailButton label={t("Sign out")} Icon={LogOut} collapsed={collapsed} onClick={onLogout} testid="logout-button" />
          {showToggle && (
            <div className="pt-1 mt-1 relative before:content-[''] before:absolute before:top-0 before:left-2 before:right-2 before:h-px before:bg-white/[0.05]">
              <RailButton label={collapsed ? t("Expand") : t("Collapse")} Icon={collapsed ? PanelLeftOpen : PanelLeftClose}
                collapsed={collapsed} onClick={onToggle} testid="sidebar-collapse-toggle" />
            </div>
          )}
          {!collapsed && (
            <div className="mt-3 pt-3 relative before:content-[''] before:absolute before:top-0 before:left-3 before:right-3 before:h-px before:bg-white/[0.05] flex flex-wrap gap-x-3 gap-y-1 px-3">
              <Link to="/legal" data-testid="sidebar-legal-link" className="text-[11px] text-white/30 hover:text-[#D4AF37] transition-colors duration-300">Legal Centre</Link>
              <Link to="/legal/terms-of-service" className="text-[11px] text-white/30 hover:text-[#D4AF37] transition-colors duration-300">Terms</Link>
              <Link to="/legal/privacy-policy" className="text-[11px] text-white/30 hover:text-[#D4AF37] transition-colors duration-300">Privacy</Link>
            </div>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
}

export default memo(SidebarInner);
